/* eslint-disable react-refresh/only-export-components -- TanStack routes export Route beside page components. */
import { useRef, useState } from "react";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { Box, Move } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { AppShell } from "@/components/shell/app-shell";
import { BackLink } from "@/components/shell/back-link";
import { RedirectToLogin } from "@/components/shell/redirect-to-login";
import { Splash } from "@/components/shell/splash";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { ElectricMark } from "@/components/plans/electric-mark";
import { OpeningMark } from "@/components/visit/plan-parts";
import { useDrawnSize } from "@/components/visit/plan-drawing";
import { boardName } from "@/i18n/catalog";
import { ELECTRIC_KINDS, countByKind } from "@/lib/electrics";
import { bbox, floorArea } from "@/lib/floor-plan";
import { layoutHouse, type PlacedRoom } from "@/lib/house-layout";
import { said } from "@/lib/errors";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/houses/$houseId")({
  component: HousePage,
});

function HousePage() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <Unauthenticated>
        <RedirectToLogin />
      </Unauthenticated>
      <Authenticated>
        <SignedInHouse />
      </Authenticated>
      <Toaster />
    </>
  );
}

type Overview = NonNullable<FunctionReturnType<typeof api.houses.overview>>;
type OverviewRoom = Overview["rooms"][number];
type Placed = PlacedRoom<OverviewRoom & { id: string; size: null }>;

/**
 * The whole house at once: each level's plan with every room where it lies
 * (moved by hand by the team), their doors, windows and electrical points,
 * then each room as a card with its 3D or its cover find.
 */
function SignedInHouse() {
  const { t } = useTranslation("plans");
  const { houseId } = useParams({ from: "/houses/$houseId" });
  const overview = useQuery(api.houses.overview, { houseId: houseId as Id<"houses"> });
  const me = useQuery(api.workspaces.getMyWorkspace);
  const [arranging, setArranging] = useState(false);

  if (overview === undefined || me === undefined) return <Splash subtitle={t("house.loading")} />;
  if (overview === null) {
    return (
      <AppShell width="wide">
        <div className="grid gap-4">
          <BackLink to="/" label={t("house.back")} />
          <p className="font-display text-xl italic text-ink-2">{t("house.notFound")}</p>
        </div>
      </AppShell>
    );
  }

  // The team, and in a design studio only roles that may change rooms.
  const isTeam = me?.role === "team" && me.can.rooms;
  const levels = layoutHouse(
    overview.rooms.map((room) => ({ ...room, id: room.boardId as string, size: null })),
  );
  const area = overview.rooms.reduce((sum, room) => sum + (room.measured ? floorArea(room.plan) : 0), 0);
  const electrics = countByKind(overview.rooms.flatMap((room) => room.electrics));
  const electricTotal = ELECTRIC_KINDS.reduce((sum, kind) => sum + electrics[kind], 0);

  return (
    <AppShell width="wide">
      <div className="grid gap-10">
        <header className="grid gap-4">
          <BackLink to="/" label={t("house.back")} />
          <span className="text-2xs uppercase tracking-[0.18em] text-ink-3">{t("house.eyebrow")}</span>
          <h1 className="font-display text-4xl tracking-tight text-ink">
            <span className="italic">{overview.house.name}</span>
            <span className="text-accent">.</span>
          </h1>
          <dl className="flex flex-wrap items-baseline gap-x-8 gap-y-2 text-xs text-ink-3">
            <Fact label={t("house.rooms")} value={String(overview.rooms.length)} />
            <Fact label={t("house.levels")} value={String(levels.length)} />
            {area > 0 ? <Fact label={t("house.area")} value={t("house.squareMetres", { value: Math.round(area) })} /> : null}
            {electricTotal > 0 ? <Fact label={t("house.electrics")} value={String(electricTotal)} /> : null}
          </dl>
          {isTeam && overview.rooms.length > 1 ? (
            <div>
              <Button variant={arranging ? "default" : "outline"} size="sm" onClick={() => setArranging((on) => !on)}>
                <Move /> {arranging ? t("house.arrangeDone") : t("house.arrange")}
              </Button>
              {arranging ? <p className="pt-2 text-xs text-ink-3">{t("house.arrangeHint")}</p> : null}
            </div>
          ) : null}
        </header>

        {levels.map(({ level, rooms, extent }) => (
          <section key={level ?? "none"} className="grid gap-3">
            <h2 className="text-2xs uppercase tracking-[0.18em] text-ink-3">{level ?? t("house.noLevel")}</h2>
            <LevelPlan rooms={rooms as Placed[]} extent={extent} arranging={arranging} />
          </section>
        ))}

        <section className="grid gap-3">
          <h2 className="text-2xs uppercase tracking-[0.18em] text-ink-3">{t("house.roomsHeading")}</h2>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {overview.rooms.map((room) => (
              <li key={room.boardId}>
                <RoomCard room={room} />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </AppShell>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-2xs uppercase tracking-wider text-ink-4">{label}</dt>
      <dd className="text-sm text-ink num tabular-nums">{value}</dd>
    </div>
  );
}

/** Snap for rooms moved by hand, in metres. */
const MOVE_STEP = 0.05;
const snapMove = (v: number) => Math.round(v / MOVE_STEP) * MOVE_STEP;

/**
 * One level, drawn to scale: every room at its place, its name in it, its
 * doors, windows and electrical points. A tap opens the room; while the team
 * arranges, a drag moves it and the place is kept.
 */
function LevelPlan({
  rooms,
  extent,
  arranging,
}: {
  rooms: Placed[];
  extent: { minX: number; minY: number; maxX: number; maxY: number };
  arranging: boolean;
}) {
  const { t } = useTranslation("plans");
  const navigate = useNavigate();
  const placeRoom = useMutation(api.houses.placeRoom);
  const svgRef = useRef<SVGSVGElement>(null);
  const drawn = useDrawnSize(svgRef);
  // The room being dragged, and how far, in metres.
  const [drag, setDrag] = useState<{ id: string; start: { x: number; y: number }; dx: number; dy: number } | null>(null);

  const width = extent.maxX - extent.minX;
  const length = extent.maxY - extent.minY;
  const pad = Math.max(1, Math.max(width, length) * 0.04);
  const vb = { x: extent.minX - pad, y: extent.minY - pad, w: width + 2 * pad, h: length + 2 * pad };
  const mpp = drawn.w > 0 ? Math.max(vb.w / drawn.w, vb.h / drawn.h) : vb.w / 800;
  const px = (n: number) => n * mpp;

  const toPlan = (event: React.PointerEvent) => {
    const matrix = svgRef.current?.getScreenCTM()?.inverse();
    if (!matrix) return null;
    const at = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix);
    return { x: at.x, y: at.y };
  };

  const drop = async (room: Placed) => {
    if (!drag || drag.id !== room.id) return;
    const moved = Math.abs(drag.dx) > MOVE_STEP / 2 || Math.abs(drag.dy) > MOVE_STEP / 2;
    setDrag(null);
    if (!moved) return;
    try {
      await placeRoom({
        boardId: room.boardId,
        place: { x: snapMove(room.at.x + drag.dx), y: snapMove(room.at.y + drag.dy) },
      });
    } catch (err) {
      toast.error(said(err, t("house.moveFailed")));
    }
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
      className="block max-h-[70svh] w-full rounded-md border border-line bg-paper-3"
      style={{ aspectRatio: `${vb.w} / ${vb.h}` }}
      role="img"
      aria-label={t("house.planLabel")}
    >
      {rooms.map((room) => {
        const box = bbox(room.plan);
        const dragging = drag?.id === room.id;
        const ox = room.at.x - box.minX + (dragging ? drag.dx : 0);
        const oy = room.at.y - box.minY + (dragging ? drag.dy : 0);
        return (
          <g
            key={room.id}
            transform={`translate(${ox} ${oy})`}
            className={cn(arranging ? "cursor-move" : "cursor-pointer", "outline-none")}
            tabIndex={arranging ? -1 : 0}
            role={arranging ? undefined : "link"}
            aria-label={boardName(room.name)}
            onClick={() => {
              if (!arranging) void navigate({ to: "/boards/$boardId", params: { boardId: room.boardId } });
            }}
            onKeyDown={(event) => {
              if (!arranging && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                void navigate({ to: "/boards/$boardId", params: { boardId: room.boardId } });
              }
            }}
            onPointerDown={(event) => {
              if (!arranging) return;
              const at = toPlan(event);
              if (!at) return;
              (event.currentTarget as Element).setPointerCapture(event.pointerId);
              setDrag({ id: room.id, start: at, dx: 0, dy: 0 });
            }}
            onPointerMove={(event) => {
              if (!drag || drag.id !== room.id) return;
              const at = toPlan(event);
              if (at) setDrag({ ...drag, dx: at.x - drag.start.x, dy: at.y - drag.start.y });
            }}
            onPointerUp={() => void drop(room)}
            onPointerCancel={() => setDrag(null)}
          >
            <polygon
              points={room.plan.map((p) => `${p.x},${p.y}`).join(" ")}
              fill={room.measured ? "var(--paper)" : "var(--paper-2)"}
              stroke={dragging ? "var(--accent)" : "var(--ink-2)"}
              strokeWidth={px(2.5)}
              strokeDasharray={room.measured ? undefined : `${px(6)} ${px(4)}`}
              strokeLinejoin="miter"
              className="transition-[fill] hover:fill-[var(--paper-2)]"
            />
            {room.openings.map((opening, i) => (
              <OpeningMark key={i} plan={room.plan} opening={opening} color="var(--ink-3)" px={px} />
            ))}
            {room.electrics.map((point) => (
              <ElectricMark key={point.id} kind={point.kind} x={point.x} y={point.y} r={px(4.5)} />
            ))}
            <text
              x={box.minX + box.width / 2}
              y={box.minY + box.length / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={px(12)}
              fill="var(--ink)"
              className="pointer-events-none select-none font-sans"
            >
              {boardName(room.name)}
            </text>
            <text
              x={box.minX + box.width / 2}
              y={box.minY + box.length / 2 + px(15)}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={px(10)}
              fill="var(--ink-3)"
              className="pointer-events-none select-none font-sans num"
            >
              {room.measured
                ? t("house.squareMetres", { value: Math.round(floorArea(room.plan) * 10) / 10 })
                : t("house.notMeasured")}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function RoomCard({ room }: { room: OverviewRoom }) {
  const { t } = useTranslation("plans");
  const counts = countByKind(room.electrics);
  const electricLine = ELECTRIC_KINDS.filter((kind) => counts[kind] > 0)
    .map((kind) => t(`electrics.counts.${kind}`, { count: counts[kind] }))
    .join(" · ");
  return (
    <Link
      to="/boards/$boardId"
      params={{ boardId: room.boardId }}
      className="group grid gap-2 rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink"
    >
      <div className="relative aspect-[3/2] overflow-hidden rounded-md border border-line bg-paper-2">
        {room.pictureUrl ? (
          <img
            src={room.pictureUrl}
            alt=""
            loading="lazy"
            className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="grid size-full place-items-center text-xs text-ink-4">{t("house.noPicture")}</div>
        )}
        {room.has3d ? (
          <span className="absolute start-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-2xs uppercase tracking-wider text-white backdrop-blur">
            <Box className="size-3" /> {t("house.has3d")}
          </span>
        ) : null}
      </div>
      <div className="grid gap-0.5">
        <p className="text-sm text-ink">
          {boardName(room.name)}
          {room.level ? <span className="text-ink-3"> · {room.level}</span> : null}
        </p>
        <p className="text-xs text-ink-3">
          {t("house.finds", { count: room.itemCount })}
          {electricLine ? ` · ${electricLine}` : ""}
        </p>
      </div>
    </Link>
  );
}
